/**
 * addon-selector.js
 *
 * Two custom elements that implement the optional-addon UX on product pages:
 *
 *  <addon-selector> — renders a trigger button + <dialog> with radio-based product list.
 *                     Dispatches `addon:selected` (bubbles) when user confirms a choice.
 *
 *  <multi-add-form> — intercepts the product form's submit event (capture phase) and
 *                     replaces it with a multi-item /cart/add.js POST that includes the
 *                     main product variant AND whichever addon variants have been
 *                     selected. Addons are optional — Add to Cart is never blocked.
 */

class AddonSelector extends HTMLElement {
  #selectedVariantId   = null;
  #gallery             = null;
  #galleryImg          = null;
  #galleryCounter      = null;
  #galleryActionSlot   = null;
  #galleryImages       = [];
  #galleryIndex        = 0;
  #galleryAlt          = '';
  #galleryMovedControl = null;
  #galleryPlaceholder  = null;
  #touchStartX         = null;
  #dialog              = null;
  #savedScrollTop      = 0;

  get selectedVariantId() {
    return this.#selectedVariantId;
  }

  get slotType() {
    return this.dataset.slotType || '';
  }

  connectedCallback() {
    const dialog     = this.querySelector('dialog');
    const triggerBtn = this.querySelector('[data-addon-trigger]');
    const closeBtn   = this.querySelector('[data-dialog-close]');
    const changeBtn  = this.querySelector('[data-addon-change]');

    triggerBtn?.addEventListener('click', () => dialog?.showModal());
    closeBtn?.addEventListener('click',   () => dialog?.close());
    changeBtn?.addEventListener('click',  () => dialog?.showModal());

    this.#dialog            = dialog;
    this.#gallery           = this.querySelector('[data-addon-gallery]');
    this.#galleryImg        = this.querySelector('[data-gallery-image]');
    this.#galleryCounter    = this.querySelector('[data-gallery-counter]');
    this.#galleryActionSlot = this.querySelector('[data-gallery-action]');

    // Reset the image gallery whenever the dialog closes — including after a
    // selection is confirmed from inside the expanded view — so the moved
    // select control is returned to its tile before the dialog reopens.
    dialog?.addEventListener('close', () => this.#closeGallery());

    // Cycle images with the keyboard while the gallery is open.
    this.addEventListener('keydown', (e) => {
      if (!this.#gallery || this.#gallery.hidden) return;
      if (e.key === 'ArrowLeft')  { e.preventDefault(); this.#stepGallery(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.#stepGallery(1); }
    });

    // Detect horizontal finger swipes to cycle images on touch devices.
    this.#gallery?.addEventListener('touchstart', (e) => {
      this.#touchStartX = e.changedTouches[0].clientX;
    }, { passive: true });
    this.#gallery?.addEventListener('touchend', (e) => {
      if (this.#touchStartX === null) return;
      const dx = e.changedTouches[0].clientX - this.#touchStartX;
      this.#touchStartX = null;
      if (Math.abs(dx) < 40) return;
      this.#stepGallery(dx < 0 ? 1 : -1);
    }, { passive: true });

    // Close on backdrop click
    dialog?.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });

    // When Escape is pressed, close any open custom dropdown first and prevent
    // the dialog from also closing on the same keypress. A second Escape will
    // then close the dialog normally via the browser's native cancel behaviour.
    dialog?.addEventListener('cancel', (e) => {
      // Escape precedence: close the open image gallery first, then any open
      // custom dropdown, and only then let the dialog close natively.
      if (this.#gallery && !this.#gallery.hidden) {
        e.preventDefault();
        this.#closeGallery();
        return;
      }
      const openList = this.#openCustomList();
      if (openList) {
        e.preventDefault();
        this.#closeCustomSelect(openList);
      }
    });

    // Single delegated click handler covering both custom-select open/close
    // and option selection (buttons for single-variant, <li> for multi-variant).
    this.addEventListener('click', (e) => {

      // ── Image gallery: open / navigate / close ──────────────────────────
      const galleryOpener = e.target.closest('[data-gallery-open]');
      if (galleryOpener) {
        this.#openGallery(galleryOpener.closest('.addon-selector__option'));
        return;
      }
      if (e.target.closest('[data-gallery-prev]'))  { this.#stepGallery(-1); return; }
      if (e.target.closest('[data-gallery-next]'))  { this.#stepGallery(1);  return; }
      if (e.target.closest('[data-gallery-close]')) { this.#closeGallery();  return; }
      // A click on the gallery backdrop (not the image, arrows or action)
      // closes it — unless a dropdown is open inside, in which case the
      // pointerdown handler closes that first and the gallery stays open.
      if (this.#gallery && !this.#gallery.hidden && !this.#openCustomList()) {
        if (e.target === this.#gallery ||
            e.target.classList.contains('addon-selector__gallery-stage')) {
          this.#closeGallery();
          return;
        }
      }

      // ── Custom select trigger: toggle the dropdown ──────────────────────
      const trigger = e.target.closest('[data-custom-select-trigger]');
      if (trigger) {
        const list = trigger.closest('[data-custom-select]')
                            ?.querySelector('[data-custom-select-list]');
        if (!list) return;
        const opening = list.hidden;
        // Always close any already-open dropdown first (only one open at a time)
        this.querySelectorAll('[data-custom-select-list]:not([hidden])')
            .forEach(l => this.#closeCustomSelect(l));
        if (opening) {
          list.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
        }
        return; // do not fall through to option-selection logic
      }

      // ── Option selection: "Select" buttons and custom <li> options ───────
      const option = e.target.closest('[data-variant-id]');
      if (!option) return;

      this.#selectedVariantId = option.dataset.variantId;
      const label = option.dataset.variantTitle || this.#selectedVariantId;

      // Close the custom dropdown if this option was inside one
      const openList = this.#openCustomList();
      if (openList) this.#closeCustomSelect(openList);

      if (triggerBtn) {
        triggerBtn.textContent = '';
        // Switch the trigger from its empty "+ Select" affordance to a filled
        // row: thumbnail + chosen name + a small sub-label.
        triggerBtn.classList.add('addon-selector__trigger--filled');

        // Prefer the variant image stored on the option element; fall back to
        // the tile's product image for single-variant "Select" buttons.
        const variantImageUrl = option.dataset.variantImage;
        if (variantImageUrl) {
          const thumb = document.createElement('img');
          thumb.src = variantImageUrl;
          triggerBtn.appendChild(thumb);
        } else {
          const optionImg = option.closest('.addon-selector__option')?.querySelector('img');
          if (optionImg) triggerBtn.appendChild(optionImg.cloneNode());
        }

        const textWrap = document.createElement('span');
        textWrap.className = 'addon-selector__chosen';
        const nameEl = document.createElement('span');
        nameEl.className = 'addon-selector__chosen-name';
        nameEl.textContent = label;
        const subEl = document.createElement('span');
        subEl.className = 'addon-selector__chosen-sub';
        // Copy comes from the markup so it stays themeable/translatable.
        subEl.textContent = this.dataset.slotNoun || 'Free gift';
        textWrap.append(nameEl, subEl);
        triggerBtn.appendChild(textWrap);
      }

      // Reveal the "change selection" affordance now that a gift is chosen.
      changeBtn?.removeAttribute('hidden');

      dialog?.close();

      this.dispatchEvent(new CustomEvent('addon:selected', {
        bubbles: true,
        detail: { variantId: this.#selectedVariantId }
      }));
    });

    // Close the open custom dropdown when the user clicks anywhere outside it
    // within the addon-selector element. Runs in the capture phase so it fires
    // before the click handler above, and is scoped to this element so it
    // cannot leak to the rest of the page.
    this.addEventListener('pointerdown', (e) => {
      const openList = this.#openCustomList();
      if (!openList) return;
      if (!openList.closest('[data-custom-select]').contains(e.target)) {
        this.#closeCustomSelect(openList);
      }
    }, true);
  }

  // Returns the currently open custom-select list, or null.
  #openCustomList() {
    return this.querySelector('[data-custom-select-list]:not([hidden])') ?? null;
  }

  // Hides the list and resets aria-expanded on its trigger button.
  #closeCustomSelect(list) {
    list.hidden = true;
    list.closest('[data-custom-select]')
        ?.querySelector('[data-custom-select-trigger]')
        ?.setAttribute('aria-expanded', 'false');
  }

  // Opens the full-popup image gallery for a given option tile. Moves that
  // tile's select control (button or variant dropdown) into the centred action
  // slot so the customer can still confirm their choice from the expanded view.
  #openGallery(optionEl) {
    if (!optionEl || !this.#gallery) return;

    // Restore any previously moved control before opening a new gallery.
    this.#closeGallery();

    let images = [];
    try { images = JSON.parse(optionEl.dataset.gallery || '[]'); } catch (e) { /* noop */ }
    if (!images.length) return;

    this.#galleryImages = images;
    this.#galleryAlt    = optionEl.dataset.galleryAlt || '';
    this.#galleryIndex  = 0;

    // Move this tile's select control into the centred action slot, leaving a
    // placeholder so it can be returned to exactly where it was on close.
    const control = optionEl.querySelector('.addon-selector__custom-select, .addon-selector__select-btn');
    if (control && this.#galleryActionSlot) {
      this.#galleryPlaceholder = document.createComment('addon-control');
      control.before(this.#galleryPlaceholder);
      this.#galleryMovedControl = control;
      this.#galleryActionSlot.appendChild(control);
    }

    // Arrows and counter are only meaningful when there is more than one image.
    const multiple = images.length > 1;
    this.querySelectorAll('[data-gallery-prev], [data-gallery-next]')
        .forEach(btn => { btn.hidden = !multiple; });
    if (this.#galleryCounter) this.#galleryCounter.hidden = !multiple;

    // Pin the dialog to the top and lock its scroll while the gallery is open,
    // so the overlay's inset:0 origin coincides with the visible popup box
    // (read scrollTop before locking, as overflow:hidden can freeze the value).
    this.#savedScrollTop = this.#dialog?.scrollTop ?? 0;
    this.#dialog?.classList.add('addon-selector__dialog--gallery-open');
    if (this.#dialog) this.#dialog.scrollTop = 0;

    this.#renderGalleryImage();
    this.#gallery.hidden = false;
  }

  #renderGalleryImage() {
    if (!this.#galleryImg) return;
    this.#galleryImg.src = this.#galleryImages[this.#galleryIndex];
    this.#galleryImg.alt = this.#galleryAlt;
    if (this.#galleryCounter) {
      this.#galleryCounter.textContent =
        `${this.#galleryIndex + 1} / ${this.#galleryImages.length}`;
    }
  }

  #stepGallery(direction) {
    const n = this.#galleryImages.length;
    if (!n) return;
    this.#galleryIndex = (this.#galleryIndex + direction + n) % n;
    this.#renderGalleryImage();
  }

  // Hides the gallery and returns any moved select control to its tile.
  #closeGallery() {
    if (!this.#gallery || this.#gallery.hidden) return;
    this.#gallery.hidden = true;
    // Unlock the dialog scroll and restore the customer's place in the grid.
    this.#dialog?.classList.remove('addon-selector__dialog--gallery-open');
    if (this.#dialog) this.#dialog.scrollTop = this.#savedScrollTop;
    if (this.#galleryMovedControl && this.#galleryPlaceholder) {
      this.#galleryPlaceholder.replaceWith(this.#galleryMovedControl);
    }
    this.#galleryMovedControl = null;
    this.#galleryPlaceholder  = null;
    this.#galleryImages       = [];
  }
}

class MultiAddForm extends HTMLElement {
  #formId          = null;
  #form            = null;
  #abortController = null;

  connectedCallback() {
    this.#formId         = this.getAttribute('form');
    this.#abortController = new AbortController();
    const { signal }     = this.#abortController;

    // Defer until the full DOM is constructed so we can reference the form
    // and buy-buttons elements that are rendered after this element in the HTML.
    setTimeout(() => {
      this.#form = document.getElementById(this.#formId);
      if (!this.#form) {
        console.warn('[MultiAddForm] Could not find form:', this.#formId);
        return;
      }

      // Listen at document level so the listener survives ProductRerender
      // replacing the buy-buttons (and therefore the <form>) DOM node.
      document.addEventListener('submit', (e) => {
        if (e.target === this.#form) {
          this.#handleSubmit(e);
        }
      }, { capture: true, signal });
    }, 0);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  // All addon-selector elements in the same Shopify section
  get #addonSelectors() {
    const section = this.closest('[id^="shopify-section"]') || document;
    return Array.from(section.querySelectorAll('addon-selector'));
  }

  // Find all ATC submit buttons associated with the form — both buttons physically
  // inside the <form> element and buttons outside it that reference the form via
  // the HTML form="..." attribute (e.g. the sticky bar button).
  #findSubmitButtons() {
    const inside  = this.#form
      ? Array.from(this.#form.querySelectorAll('[type="submit"]'))
      : [];
    const outside = this.#formId
      ? Array.from(document.querySelectorAll(`[type="submit"][form="${this.#formId}"]`))
      : [];
    // Deduplicate in case a button somehow matches both selectors
    return [...new Set([...inside, ...outside])];
  }

  async #handleSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const form = this.#form;
    if (!form) return;

    const mainVariantId = form.querySelector('[name="id"]')?.value;
    if (!mainVariantId) return;

    // Preserve the on_success directive (e.g. "force_open_drawer") from the original form
    const onSuccessDo = form.querySelector('[name="on_success"]')?.value || null;

    const addedAt   = Date.now().toString();
    const selectors = this.#addonSelectors;
    const giftItems = selectors
      .filter(s => s.selectedVariantId)
      .map(s => {
        if (!s.slotType) {
          console.warn('[MultiAddForm] Gift selector missing slot type; this gift will not be groupable under its bag.');
        }
        return {
          id: parseInt(s.selectedVariantId, 10),
          quantity: 1,
          properties: {
            '_gift_slot_type': s.slotType,
            '_gift_parent_variant_id': mainVariantId,
            '_gift_added_at': addedAt
          }
        };
      });

    // Allow cart-aware sections (cart-drawer, cart-notification, etc.) to register
    // themselves for bundled re-render in the same request.
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const submitBtns = this.#findSubmitButtons();
    submitBtns.forEach(btn => btn.setAttribute('aria-busy', 'true'));

    const items = [
      { id: parseInt(mainVariantId, 10), quantity: 1 },
      ...giftItems
    ];

   // console.log('[MultiAddForm] Intercepted submit, items:', items);

    const fetchBody = { items };
    if (sectionsToBundle.length > 0) {
      fetchBody.sections = sectionsToBundle.join(',');
      fetchBody.sections_url = window.Shopify.routes.root + 'variants/' + mainVariantId;
    }

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(fetchBody)
      });

      submitBtns.forEach(btn => btn.removeAttribute('aria-busy'));
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      const responseJson = await response.json();

      if (response.ok) {
        console.log('[MultiAddForm] Cart add successful:', responseJson);
        // Redirect to cart page if cart type is set to "page"
        if (window.themeVariables?.settings?.cartType === 'page'
            || window.themeVariables?.settings?.pageType === 'cart') {
          window.location.href = window.Shopify.routes.root + 'cart';
          return;
        }

        // Fetch the updated cart and attach the bundled section renders
        const cartJson = await fetch(window.Shopify.routes.root + 'cart.js').then(r => r.json());
        cartJson['sections'] = responseJson['sections'];

        // Replicate the event contract that ProductForm normally emits so that
        // BuyButtons, CartDrawer, CartNotification, etc. all react correctly.
        form.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: Object.prototype.hasOwnProperty.call(responseJson, 'items')
              ? responseJson['items']
              : [responseJson],
            cart: cartJson,
            onSuccessDo
          }
        }));

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', onSuccessDo, cart: cartJson }
        }));

        document.dispatchEvent(new CustomEvent('cart:refresh'));

      } else {
        console.error('[MultiAddForm] Cart add failed:', response.status, responseJson);
        form.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: responseJson['description'] || 'An error occurred adding to cart.' }
        }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }

    } catch (err) {
      submitBtns.forEach(btn => btn.removeAttribute('aria-busy'));
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }
}

/**
 * <addon-progress> — drives the free-gifts progress UI for the <addon-selector>
 * elements in the same Shopify section (the single source of truth for state).
 * As each selector is confirmed it fills one segment of a segmented meter
 * (one segment per slot, derived in Liquid — never hardcoded), updates a
 * singular/plural count line ("1 of 2 free gifts chosen"), and on completion
 * toggles the header from its pending element to the complete element (tick).
 * Solo (e.g. product.strap-solo) simply omits the segments and shows the
 * header + count line only. Copy is supplied via data-* attributes so nothing
 * is hardcoded here: data-noun-singular / data-noun-plural / data-complete-subtext
 * (and the legacy data-pending-text / data-complete-text textContent fallback).
 */
class AddonProgress extends HTMLElement {
  #scope            = null;
  #segments         = [];
  #count            = null;
  #label            = null;
  #pendingEl        = null;
  #completeEl       = null;
  #pendingText      = '';
  #completeText     = '';
  #nounSingular     = '';
  #nounPlural       = '';
  #completeSubtext  = '';
  #abortController  = null;

  connectedCallback() {
    this.#scope           = this.closest('[id^="shopify-section"]') || document;
    this.#segments        = Array.from(this.querySelectorAll('[data-progress-fill]'));
    this.#count           = this.querySelector('[data-progress-count]');
    this.#label           = this.querySelector('[data-progress-label]');
    this.#pendingEl       = this.querySelector('[data-progress-pending]');
    this.#completeEl      = this.querySelector('[data-progress-complete]');
    this.#pendingText     = this.dataset.pendingText     || '';
    this.#completeText    = this.dataset.completeText    || '';
    this.#nounSingular    = this.dataset.nounSingular    || 'free gift';
    this.#nounPlural      = this.dataset.nounPlural      || 'free gifts';
    this.#completeSubtext = this.dataset.completeSubtext || '';

    this.#abortController = new AbortController();
    this.#scope.addEventListener('addon:selected',
      () => this.#update(),
      { signal: this.#abortController.signal }
    );

    this.#update();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #update() {
    const selectors = Array.from(this.#scope.querySelectorAll('addon-selector'));
    const total     = selectors.length;
    // Loose != null catches both null (upgraded, unselected) and undefined (not yet upgraded)
    const selected  = selectors.filter(s => s.selectedVariantId != null).length;
    const complete  = total > 0 && selected === total;

    // Segmented meter: fill the first `selected` segments. Solo has no segments,
    // so this is a no-op there.
    this.#segments.forEach((seg, i) => {
      seg.classList.toggle('addon-progress__segment--filled', i < selected);
    });

    // Count line, singular/plural aware on the slot total.
    if (this.#count) {
      if (complete && this.#completeSubtext) {
        this.#count.textContent = this.#completeSubtext;
      } else {
        const noun = total === 1 ? this.#nounSingular : this.#nounPlural;
        this.#count.textContent = `${selected} of ${total} ${noun} chosen`;
      }
    }

    // Header: prefer toggling the rich pending/complete elements (so the grey
    // note and the completion tick keep their markup); fall back to a plain
    // textContent swap when only the data-* strings are provided.
    if (this.#pendingEl && this.#completeEl) {
      this.#pendingEl.hidden  = complete;
      this.#completeEl.hidden = !complete;
    } else if (this.#label && this.#pendingText && this.#completeText) {
      this.#label.textContent = complete ? this.#completeText : this.#pendingText;
    }

    this.setAttribute('aria-valuemax', total);
    this.setAttribute('aria-valuenow', selected);
    this.toggleAttribute('data-complete', complete);
  }
}

if (!customElements.get('addon-selector')) {
  customElements.define('addon-selector', AddonSelector);
}
if (!customElements.get('multi-add-form')) {
  customElements.define('multi-add-form', MultiAddForm);
}
if (!customElements.get('addon-progress')) {
  customElements.define('addon-progress', AddonProgress);
}
